import React, { useState, useEffect, useRef, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import { LinearProgress } from "@mui/material";
import { Icon } from "semantic-ui-react";
import CardDiagFormQuestion from "./CardDiagFormQuestion";
import ButtonSubmitBottom from "./ButtonSubmitBottom";
import { prepareForm } from "../Common/Diag";

// ===== TYPE DEFINITIONS =====
type TreeNode = {
  id: number;
  parent_id: number | null;
  level: number;
  span: number;
  label: string;
  text: string;
  condition: string;
  type: "choices" | "end" | "root";
  selected?: boolean;
  answer?: string;
  extra_finished?: boolean;
  extra_items?: any[];
  extra_intro?: string;
  extra_submit?: string;
  triage_level?: string;
};

type PhysicalAssessmentDiagFormProps = {
  data?: Record<string, any>;
  loading?: boolean;
  onSubmit?: (formData: Record<string, any>, lastNode: any) => void;
};

// ===== CONSTANTS =====
const COLORS = {
  grey: "#3C3C3C",
  pink: "var(--primary-theme-color)",
  lightGrey: "#999999",
  textGrey: "#666666",
};

// ===== STYLES =====
const useStyles = makeStyles(() => ({
  progressContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    marginBottom: "1rem",
  },
  progressBar: {
    "&.MuiLinearProgress-root": {
      height: "8px",
      borderRadius: "10px",
      backgroundColor: "rgba(217, 217, 217, 1)",
      width: "100%",
      "& .MuiLinearProgress-bar": {
        backgroundColor: COLORS.pink,
        borderRadius: "10px",
      },
    },
  },
  previousQuestion: {
    marginBottom: "16px",
    pointerEvents: "none",
  },
  questionContainer: {
    flex: 1,
    marginBottom: "24px",
  },
}));

const PhysicalAssessmentDiagForm = (props: PhysicalAssessmentDiagFormProps) => {
  const classes = useStyles();
  const currentQuestionRef = useRef<HTMLDivElement>(null);

  const [questionPath, setQuestionPath] = useState<number[]>([0]);
  const [treeData, setTreeData] = useState<Record<string, any>>({});

  useEffect(() => {
    const diagRule = { content: JSON.stringify(props.data ?? {}) };
    setTreeData(prepareForm(diagRule));
  }, [props.data]);

  const currentNodeId = useMemo(() => questionPath[questionPath.length - 1], [questionPath]);

  const currentNode = useMemo(() => treeData[currentNodeId], [treeData, currentNodeId]);

  // Get child nodes of a given node
  const getChildNodes = (nodeId: number): TreeNode[] => {
    return Object.values(treeData).filter((node: any) => node.parent_id === nodeId) as any[];
  };

  // Calculate progress based on tree depth
  const progress = useMemo(() => {
    if (!currentNode) return 0;
    if (currentNode.type === "end") return 100;

    const maxLevel = Math.max(...Object.values(treeData).map((node: any) => node.level || 0));
    const currentLevel = currentNode.level;
    const calculatedProgress = maxLevel > 0 ? (currentLevel / maxLevel) * 100 : 0;

    return Math.min(95, Math.max(10, calculatedProgress));
  }, [currentNode, treeData]);

  const getExtraItemFromNode = (nodeId: number) => {
    const node = treeData[nodeId];
    if (!node) return undefined;

    return {
      label: node.text,
      type: "Select" as const,
      value: node?.answer || "",
      nullOk: false,
      options: getChildNodes(nodeId).map((child) => child.condition),
    };
  };

  const endResult = useMemo(() => {
    const lastNodeInPath = treeData[questionPath[questionPath.length - 1]];
    const isEnd =
      lastNodeInPath?.type === "end" ||
      !lastNodeInPath?.text ||
      getChildNodes(currentNodeId).length === 0;

    return { isEnd, lastNode: lastNodeInPath };
  }, [currentNode, treeData, questionPath, currentNodeId]);

  const previousQuestions = useMemo(() => {
    return questionPath
      .slice(0, -1)
      .map((nodeId) => {
        const node = treeData[nodeId];
        if (!node || node.type === "end" || !node.text) return null;
        return nodeId;
      })
      .filter((id): id is number => id !== null);
  }, [questionPath, treeData]);

  const isCurrentQuestionAnswered = useMemo(() => {
    const answer = currentNode?.answer;
    return answer !== undefined && answer !== null && answer !== "";
  }, [currentNode]);

  const handleRadioChange = (nodeId: number, value: string) => {
    setTreeData((prev: any) => {
      const newTreeData = JSON.parse(JSON.stringify(prev));
      if (newTreeData[nodeId]) {
        newTreeData[nodeId].answer = value;
        newTreeData[nodeId].selected = true;
      }
      return newTreeData;
    });
  };

  const handleNext = () => {
    if (endResult.isEnd) {
      props.onSubmit?.(treeData, endResult.lastNode);
      return;
    }

    const selectedAnswer = currentNode?.answer;
    if (!selectedAnswer) return;

    const children = getChildNodes(currentNodeId);
    const nextNode = children.find((child) => child.condition === selectedAnswer);

    if (!nextNode) return;
    setQuestionPath((prev) => [...prev, nextNode.id]);
  };

  const handleBack = () => {
    if (questionPath.length > 1) {
      setQuestionPath((prev) => prev.slice(0, -1));
    }
  };

  useEffect(() => {
    if (questionPath.length > 1) {
      setTimeout(() => {
        document.body.scrollTo({
          behavior: "smooth",
          top: document.body.offsetHeight + 1000,
        });
      }, 100);
    }
  }, [questionPath.length]);

  return (
    <div className="content-detail">
      <div style={{ padding: "24px 15px 24px" }}>
        <div className={classes.progressContainer}>
          <div style={{ marginTop: "2px", paddingRight: "0.25rem" }} onClick={handleBack}>
            <Icon name="chevron left" style={{ fontSize: "1.55rem", color: COLORS.pink }} />
          </div>
          <LinearProgress variant="determinate" value={progress} className={classes.progressBar} />
        </div>

        {previousQuestions.length > 0 && (
          <div style={{ marginBottom: "1rem" }}>
            {previousQuestions.map((nodeId) => (
              <div key={nodeId} className={classes.previousQuestion}>
                <CardDiagFormQuestion
                  extraItem={getExtraItemFromNode(nodeId)}
                  itemIndex={nodeId}
                  hideQuestionNumber
                  showOnlySelected={true}
                  onExtraItemChange={handleRadioChange}
                />
              </div>
            ))}
          </div>
        )}

        {!endResult.isEnd && currentNode?.text && (
          <div ref={currentQuestionRef} className={classes.questionContainer}>
            <CardDiagFormQuestion
              extraItem={getExtraItemFromNode(currentNodeId)}
              itemIndex={currentNodeId}
              hideQuestionNumber
              onExtraItemChange={handleRadioChange}
            />
          </div>
        )}

        <ButtonSubmitBottom
          title={endResult.isEnd ? "ส่งแบบประเมิน" : "ถัดไป"}
          onClick={handleNext}
          loading={props.loading}
          disabled={!endResult.isEnd && !isCurrentQuestionAnswered}
        />
      </div>
    </div>
  );
};

export default PhysicalAssessmentDiagForm;
